import { Box, Dialog, DialogContent, Grid, IconButton, Typography, DialogActions, Button, Divider } from '@mui/material';
import { useState, useCallback, useLayoutEffect, useRef } from 'react';
import machineDefaultImage from '../../images/machineImageEmpty.svg';
import { entityGridPhotoUrl } from '../entityImage/entityImageConfig';
import { useTranslation } from 'react-i18next';
import Iconify from '../Iconify';
import '../../css/machines/SitePoleFilterDialog.css';

const SitePoleFilterDialog = ({ openSitePoleFilterDialog, setOpenSitePoleFilterDialog, filters, setFilters, sites, poles, totalPoleMetiers, totalPoleMachines }) => {

    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [hoveredSite, setHoveredSite] = useState(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const checkScrollButtons = useCallback(() => {
        const container = containerRef.current;
        if (container) {
            setShowScrollButtons(container.scrollWidth > container.clientWidth);
        }
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;

        const resizeObserver = new ResizeObserver(checkScrollButtons);
        if (container) {
            resizeObserver.observe(container);
        }

        // Initial check with a slight delay to ensure content is loaded
        setTimeout(checkScrollButtons, 100);

        // Add window resize event listener
        window.addEventListener('resize', checkScrollButtons);

        return () => {
            if (container) {
                resizeObserver.unobserve(container);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkScrollButtons);
        };
    }, [checkScrollButtons]);

    useLayoutEffect(() => {
        checkScrollButtons();
    }, [sites, checkScrollButtons]);

    const handleScrollFilterBar = useCallback((direction) => {
        const scrollAmount = 250;
        if (containerRef.current) {
            containerRef.current.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    }, []);

    const handleChangeSite = useCallback((site) => {
        setFilters(prev => ({
            ...prev,
            selectedSite: filters.selectedSite?.id === site.id ? '' : site,
            selectedPole: '',
            selectedMetier: ''
        }));
    }, [filters.selectedSite, setFilters]);

    const handleChangePole = useCallback((pole) => {
        setFilters(prev => ({
            ...prev,
            selectedPole: pole,
            selectedMetier: ''
        }));
        setOpenSitePoleFilterDialog(false);
    }, [setFilters, setOpenSitePoleFilterDialog]);

    return (
        <Dialog open={openSitePoleFilterDialog} onClose={() => setOpenSitePoleFilterDialog(false)} maxWidth="lg" fullWidth>
            <IconButton onClick={() => setOpenSitePoleFilterDialog(false)} className='addUser_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addUser_close_icon' />
            </IconButton>
            <DialogContent className='SitePoleFilterDialog_DialogContent'>
                <Box>
                    <Typography className='SitePoleFilterDialog_title'>Sélectionner un site</Typography>
                    <Box className="SitePoleFilterDialog_sites_arrow_box">
                        {showScrollButtons && (
                            <Iconify icon={'tabler:arrow-left'} className="SitePoleFilterDialog_scroll_button left" onClick={() => handleScrollFilterBar('left')} />
                        )}
                        <Box className="SitePoleFilterDialog_sites_box" ref={containerRef}>
                            {sites?.map(site => (
                                <Box
                                    key={site.id}
                                    onClick={() => handleChangeSite(site)}
                                    className="SitePoleFilterDialog_oneSite_box"
                                    onMouseEnter={() => setHoveredSite(site.id)}
                                    onMouseLeave={() => setHoveredSite(null)}
                                >
                                    <img src={entityGridPhotoUrl(site, 'site', { fallback: machineDefaultImage })} alt={site.name} className={filters?.selectedSite?.id === site.id ? "SitePoleFilterDialog_oneSite_img selected" : "SitePoleFilterDialog_oneSite_img"} loading='lazy' decoding='async' />
                                    <Typography
                                        className={filters?.selectedSite?.id === site.id ? "SitePoleFilterDialog_oneSite_name selected" : "SitePoleFilterDialog_oneSite_name"}
                                    >
                                        {site.name}
                                    </Typography>
                                    {filters?.selectedSite?.id === site.id && (
                                        <Iconify
                                            icon={hoveredSite === site.id ? 'tabler:x' : 'tabler:check'}
                                            className="SitePoleFilterDialog_check_icon"
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                        {showScrollButtons && (
                            <Iconify icon={'tabler:arrow-right'} className="SitePoleFilterDialog_scroll_button right" onClick={() => handleScrollFilterBar('right')} />
                        )}
                    </Box>
                </Box>

                {!filters.selectedSite ? <Typography className='SitePoleFilterDialog_SelectionneSite_text'>Sélectionnez un site dans la liste, pour afficher les pôles disponibles</Typography>
                    :
                    <Box className='SitePoleFilterDialog_poles_main_box'>
                        <Typography className='SitePoleFilterDialog_title'>{poles.length} pôles disponibles dans le site "{filters?.selectedSite?.name}"</Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box className='SitePoleFilterDialog_poles_tous_arrow_box'>
                                    <Box className='SitePoleFilterDialog_poles_tous_metier_box'>
                                        <Typography className='SitePoleFilterDialog_poles_tous_text'>Tout</Typography>
                                        <Box className='SitePoleFilterDialog_poles_number_metier_machine_box'>
                                            <Typography className='SitePoleFilterDialog_poles_number_text'>{totalPoleMetiers} métiers</Typography>
                                            <Divider orientation="vertical" variant="middle" flexItem className='SitePoleFilterDialog_poles_divider' />
                                            <Typography className='SitePoleFilterDialog_poles_number_text'>{totalPoleMachines} machines</Typography>
                                        </Box>
                                    </Box>
                                    <Iconify icon={'tabler:arrow-right'} className="SitePoleFilterDialog_poles_arrow_icon" />
                                </Box>
                            </Grid>
                            {poles && poles?.map(pole => (
                                <Grid item xs={12} sm={6} md={4} key={pole.id}>
                                    <Box className='SitePoleFilterDialog_onePole_main_box' onClick={() => handleChangePole(pole)}>
                                        <img src={entityGridPhotoUrl(pole, 'pole', { fallback: machineDefaultImage })} alt={pole?.name} className={filters?.selectedPole?.id === pole?.id ? 'SitePoleFilterDialog_onePole_img selected' : 'SitePoleFilterDialog_onePole_img'} />
                                        <Box className='SitePoleFilterDialog_poles_name_metier_box'>
                                            <Typography className={filters?.selectedPole?.id === pole?.id ? 'SitePoleFilterDialog_onePole_name selected' : 'SitePoleFilterDialog_onePole_name'}>{pole?.name}</Typography>
                                            <Box className='SitePoleFilterDialog_poles_number_metier_machine_box'>
                                                <Typography className='SitePoleFilterDialog_poles_number_text'>{pole?.site_pole_metier?.length} métiers</Typography>
                                                <Divider orientation="vertical" variant="middle" flexItem className='SitePoleFilterDialog_poles_divider' />
                                                <Typography className='SitePoleFilterDialog_poles_number_text'>{pole?._count?.machine} machines</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                }
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', padding: '20px', width: '100%', borderTop: '1px solid #F2F2F2' }}>
                <Button className='addUser_button_valider' onClick={() => setOpenSitePoleFilterDialog(false)}>{t('Valider')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default SitePoleFilterDialog